'use strict';

import moment from 'moment-timezone';

// Alberta and BC stopped changing their clocks on 2026-11-01 and adopted new official names: ABT in Alberta, PCT in British Columbia.
const PERMANENT_ABBREVIATIONS = {
  'America/Edmonton': 'ABT',
  'America/Vancouver': 'PCT'
};

// standardOffset is minutes west of UTC, matching moment-timezone's zone.offsets.
const ABBREVIATION_FAMILIES = {
  'America/St_Johns': {standard: 'NST', daylight: 'NDT', standardOffset: 210},
  'America/Halifax': {standard: 'AST', daylight: 'ADT', standardOffset: 240},
  'America/Toronto': {standard: 'EST', daylight: 'EDT', standardOffset: 300},
  'America/Winnipeg': {standard: 'CST', daylight: 'CDT', standardOffset: 360},
  'Europe/London': {standard: 'GMT', daylight: 'BST', standardOffset: 0}
};

const nativeZoneAbbr = moment.fn.zoneAbbr;
const nativeZoneName = moment.fn.zoneName;

let isPatched = false;

function resolveAbbreviation(mom, fallback) {
  const zone = mom.tz();

  if (PERMANENT_ABBREVIATIONS[zone]) {
    return PERMANENT_ABBREVIATIONS[zone];
  }

  const family = ABBREVIATION_FAMILIES[zone];

  if (!family) {
    return fallback.call(mom);
  }

  return -mom.utcOffset() === family.standardOffset
    ? family.standard
    : family.daylight;
}

/**
 * Install a token resolver that later `moment.fn.<name> = ...` assignments
 * cannot clobber.
 *
 * A duplicate moment-timezone copy nested under another dependency re-wraps
 * these on the shared moment instance when it loads, which would silently
 * replace our resolver with its own (older) zone data. The setter swallows
 * those re-wraps rather than throwing, since they run in strict mode.
 *
 * @param  {String}   name     - 'zoneAbbr' or 'zoneName'
 * @param  {Function} fallback - the original moment-timezone implementation
 * @return {void}
 */
function define(name, fallback) {
  function resolver() {
    return resolveAbbreviation(this, fallback);
  }

  Object.defineProperty(moment.fn, name, {
    configurable: true,
    get: function() {
      return resolver;
    },
    set: function() {}
  });
}

/**
 * Wrap the `z` and `zz` format tokens so each zone renders its own name.
 * Call once at app startup.
 *
 * @return {void}
 */
export default function patchTimezoneAbbreviations() {
  if (isPatched) {
    return;
  }
  isPatched = true;

  define('zoneAbbr', nativeZoneAbbr);
  define('zoneName', nativeZoneName);
}
